import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import PostHelmet from "../components/PostHelmet";

/**
 * Resolves a short-code URL (/s/abc) to the canonical /post/{slug}
 * URL and replaces the current history entry so the back button
 * doesn't bounce the reader back through this page.
 *
 * Open Graph scrapers (WhatsApp / FB / Twitter / etc.) never reach
 * this component — the backend's SPA fallback intercepts /s/{code}
 * for scraper user-agents and injects the post's real OG tags.
 */
export default function ShortLinkResolver() {
  const { code } = useParams();
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/short/${encodeURIComponent(code || "")}`)
      .then((r) => {
        if (!alive) return;
        const data = r.data || {};
        // Populate Helmet first so any scraper that DOES run JS (some
        // X/Twitter and LinkedIn previews) sees post-specific tags
        // before we redirect.
        setMeta(data);
        const target = data?.path || "/";
        // Brief delay so React can flush the Helmet tags before navigating.
        // Most users won't notice; scrapers benefit.
        setTimeout(() => {
          window.location.replace(target);
        }, 50);
      })
      .catch(() => {
        if (alive) setError("Short link not found.");
      });
    return () => {
      alive = false;
    };
  }, [code]);

  return (
    <div
      className="flex min-h-[60vh] items-center justify-center px-4"
      data-testid="short-link-resolver"
    >
      {meta ? (
        <PostHelmet
          title={meta.title}
          description={meta.description}
          image={
            meta.cover_image ||
            (meta.id ? `/api/og/post/${meta.id}.png` : undefined)
          }
          url={
            typeof window !== "undefined" && meta.slug
              ? `${window.location.origin}/post/${meta.slug}`
              : undefined
          }
          type="article"
        />
      ) : null}
      <div className="text-center">
        {error ? (
          <>
            <h1 className="font-display text-2xl font-black text-[var(--brand-ink)]">
              {error}
            </h1>
            <p className="mt-2 text-sm text-[var(--brand-body)]">
              The link you followed may have expired or been removed.
            </p>
            <a
              href="/"
              data-testid="short-link-home"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:opacity-90"
            >
              Back to LAST
            </a>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-accent" />
            <p className="mt-3 text-sm text-[var(--brand-body)]">
              Taking you to the post…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
